const FROZEN_TASK_TYPES = new Set(['share', 'create', 'action'])

/** Share, create and action tasks can carry fan_task_freezes; watch has no freeze. */
export function isTaskProgressFrozen(
  taskType: string | undefined,
  isFrozen?: boolean,
  frozenUntil?: string
): boolean {
  if (!FROZEN_TASK_TYPES.has((taskType ?? '').trim().toLowerCase())) {
    return false
  }

  return isTaskFrozenActive(isFrozen, frozenUntil)
}

/** @deprecated Use `isTaskProgressFrozen`. */
export function isShareTaskProgressFrozen(
  taskType: string | undefined,
  isFrozen?: boolean,
  frozenUntil?: string
): boolean {
  return isTaskProgressFrozen(taskType, isFrozen, frozenUntil)
}

/** Why a task froze for the fan, as far as the copy of its banner cares. */
export type TaskFreezeCause = 'moderation' | 'timeout' | 'attempts' | 'other'

// The tasks service freezes a share/create task after the Nth parser
// rejection; the frozen row keeps that rejection as its failure_reason.
const MODERATION_FAILURE_REASONS: ReadonlySet<string> = new Set([
  'moderation_rejected',
  'post_not_found',
  'unsupported_video_source'
])

const TIMEOUT_FAILURE_REASONS: ReadonlySet<string> = new Set([
  'reservation_timeout',
  'moderation_timeout'
])

// An action task freezes once its retryable attempts run out; the frozen row
// keeps the reason of the last failed attempt.
const ACTION_ATTEMPT_FAILURE_REASONS: ReadonlySet<string> = new Set([
  'attempt_expired',
  'install_not_confirmed',
  'signup_not_confirmed'
])

export function taskFreezeCause(
  failureReason?: string | null
): TaskFreezeCause {
  const reason = (failureReason ?? '').trim().toLowerCase()
  if (MODERATION_FAILURE_REASONS.has(reason)) {
    return 'moderation'
  }
  if (TIMEOUT_FAILURE_REASONS.has(reason)) {
    return 'timeout'
  }
  if (ACTION_ATTEMPT_FAILURE_REASONS.has(reason)) {
    return 'attempts'
  }
  return 'other'
}

export function isTaskFrozenActive(
  isFrozen?: boolean | null,
  frozenUntil?: string | null,
  nowMs = Date.now()
): boolean {
  if (isFrozen !== true) {
    return false
  }

  const untilRaw = (frozenUntil ?? '').trim()
  if (!untilRaw) {
    return true
  }

  const untilMs = new Date(untilRaw).getTime()
  if (!Number.isFinite(untilMs)) {
    return true
  }

  return untilMs > nowMs
}
